import React, { useLayoutEffect, useMemo, useRef } from 'react';
import type { IAggregateResult } from '../../lib/mockupLogsDashboard/types';
import { drawDonut, drawDonutCompare, drawLine, drawSpark, type IExtraTooltipRow, type ILineSeries, type ISlice } from '../../lib/mockupLogsDashboard/charts';
import { alignComparison, fmt, fmtPct, pct, shortDate } from '../../lib/mockupLogsDashboard/utils';
import type { DashboardPalette } from '../../lib/mockupLogsDashboard/palette';

interface Props {
  curr: IAggregateResult;
  prev: IAggregateResult | null;
  resizeTick?: number;
  themeMode?: 'light' | 'dark';
  palette: DashboardPalette;
}

function InfoTip({ text }: { text: string }) {
  return (
    <button type="button" className="info-btn" data-tip={text} aria-label={text}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2" />
        <circle cx="7" cy="4.2" r="0.85" fill="currentColor" />
        <path d="M7 6.5 V10.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    </button>
  );
}

function DeltaBadge({ curr, prev }: { curr: number; prev: number | null | undefined }) {
  if (prev === undefined || prev === null || prev === 0) return null;
  const d = ((curr - prev) / prev) * 100;
  if (Number.isNaN(d)) return null;
  const dir = d > 0.05 ? 'up' : d < -0.05 ? 'down' : 'flat';
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '–';
  let cls: string = 'flat';
  if (dir === 'up') cls = 'up';
  else if (dir === 'down') cls = 'down';
  return (
    <span className={`delta ${cls}`} title="vs. previous period">
      {arrow} {Math.abs(d).toFixed(1)}%
    </span>
  );
}

const TIPS = {
  funnel:
    'Volume of sessions at each funnel stage. Solid bars = current selection. When compare is enabled, dashed outlines show the previous period scaled to the same total-sessions axis so you can see shape changes, not just volume.',
  rates: 'Step-to-step conversion rates. Δ shows the change in percentage points vs. the previous period (positive = improvement).',
  sessions: 'Total sessions vs. sessions that produced events (engaged). Dashed lines show the previous period when compare is enabled.',
  engagedRate: 'Daily share of sessions that produced at least one event (Engaged rate %). A drop signals friction earlier in the flow — users open the tool but don\'t interact. Dashed line = previous period when compare is on.',
  orders: 'Daily Add-to-cart events and Orders. Dashed lines show the previous period when compare is enabled.',
  rateTrend: 'Daily conversion rate at two key transitions. Watch for sustained drops as early reliability indicators.',
  events: 'Composition of event types in the current selection. Inner ring shows the previous period (when compare is on) so you can spot composition shifts.',
  tblDaily:
    'Per-day metrics for the current selection. Each row reflects one calendar day; the ↳ row beneath shows the matched day from the previous period (when compare is on).',
};

const MockupLogsConversion: React.FC<Props> = ({ curr, prev, resizeTick = 0, themeMode = 'light', palette }) => {
  const s = curr.sessionStats;
  const p = prev?.sessionStats ?? null;
  const days = curr.days;
  const prevAligned = useMemo(() => (prev ? alignComparison(days, prev.days) : null), [days, prev]);

  const refSessions = useRef<HTMLCanvasElement>(null);
  const refEngaged = useRef<HTMLCanvasElement>(null);
  const refOrders = useRef<HTMLCanvasElement>(null);
  const refRates = useRef<HTMLCanvasElement>(null);
  const refEvents = useRef<HTMLCanvasElement>(null);
  const sparkRefs = [useRef<HTMLCanvasElement>(null), useRef<HTMLCanvasElement>(null), useRef<HTMLCanvasElement>(null), useRef<HTMLCanvasElement>(null)];

  const orderRate = pct(s.sessionsWithOrders, s.totalSessions);
  const orderRatePrev = p ? pct(p.sessionsWithOrders, p.totalSessions) : null;

  useLayoutEffect(() => {
    const sSeries: ILineSeries[] = [
      { name: 'Total sessions', color: palette.primary, data: days.map((d) => ({ x: shortDate(d.statDate), y: Number(d.sessionStats[0].totalSessions) })) },
      { name: 'Engaged', color: palette.success, data: days.map((d) => ({ x: shortDate(d.statDate), y: Number(d.sessionStats[0].sessionsWithEvents) })) },
    ];
    if (prevAligned) {
      sSeries.push({
        name: 'Total (prev)',
        color: palette.primary,
        dashed: true,
        data: prevAligned.map((d, i) => ({ x: shortDate(days[i].statDate), y: Number(d.sessionStats[0].totalSessions) })),
      });
      sSeries.push({
        name: 'Engaged (prev)',
        color: palette.success,
        dashed: true,
        data: prevAligned.map((d, i) => ({ x: shortDate(days[i].statDate), y: Number(d.sessionStats[0].sessionsWithEvents) })),
      });
    }
    const sessionsExtra: IExtraTooltipRow[] = [
      {
        label: 'Engaged rate',
        color: palette.success,
        values: days.map((d) => {
          const ss = d.sessionStats[0];
          return fmtPct(pct(Number(ss.sessionsWithEvents), Number(ss.totalSessions)), 1);
        }),
      },
    ];
    if (prevAligned) {
      sessionsExtra.push({
        label: 'Engaged rate (prev)',
        color: palette.neutral,
        values: prevAligned.map((d) => {
          const ss = d.sessionStats[0];
          return fmtPct(pct(Number(ss.sessionsWithEvents), Number(ss.totalSessions)), 1);
        }),
      });
    }
    if (refSessions.current) drawLine(refSessions.current, sSeries, { extraRows: sessionsExtra });

    const engSeries: ILineSeries[] = [
      {
        name: 'Engaged rate',
        color: palette.success,
        data: days.map((d) => {
          const ss = d.sessionStats[0];
          return { x: shortDate(d.statDate), y: pct(Number(ss.sessionsWithEvents), Number(ss.totalSessions)) };
        }),
      },
    ];
    if (prevAligned) {
      engSeries.push({
        name: 'Engaged rate (prev)',
        color: palette.success,
        dashed: true,
        data: prevAligned.map((d, i) => {
          const ss = d.sessionStats[0];
          return { x: shortDate(days[i].statDate), y: pct(Number(ss.sessionsWithEvents), Number(ss.totalSessions)) };
        }),
      });
    }
    const engExtra: IExtraTooltipRow[] = [
      {
        label: 'Engaged sessions',
        color: palette.success,
        values: days.map((d) => String(Number(d.sessionStats[0].sessionsWithEvents).toLocaleString('en-US'))),
      },
      {
        label: 'Total sessions',
        color: palette.primary,
        values: days.map((d) => String(Number(d.sessionStats[0].totalSessions).toLocaleString('en-US'))),
      },
    ];
    if (prevAligned) {
      engExtra.push({
        label: 'Engaged rate (prev)',
        color: palette.neutral,
        values: prevAligned.map((d) => {
          const ss = d.sessionStats[0];
          return fmtPct(pct(Number(ss.sessionsWithEvents), Number(ss.totalSessions)), 1);
        }),
      });
    }
    if (refEngaged.current) drawLine(refEngaged.current, engSeries, { yFmt: (v) => v.toFixed(1) + '%', extraRows: engExtra });

    const oSeries: ILineSeries[] = [
      { name: 'Add to cart', color: palette.warning, data: days.map((d) => ({ x: shortDate(d.statDate), y: Number(d.sessionStats[0].sessionsWithAddToCart) })) },
      { name: 'Orders', color: palette.success, data: days.map((d) => ({ x: shortDate(d.statDate), y: Number(d.sessionStats[0].sessionsWithOrders) })) },
    ];
    if (prevAligned) {
      oSeries.push({
        name: 'Cart (prev)',
        color: palette.warning,
        dashed: true,
        data: prevAligned.map((d, i) => ({ x: shortDate(days[i].statDate), y: Number(d.sessionStats[0].sessionsWithAddToCart) })),
      });
      oSeries.push({
        name: 'Orders (prev)',
        color: palette.success,
        dashed: true,
        data: prevAligned.map((d, i) => ({ x: shortDate(days[i].statDate), y: Number(d.sessionStats[0].sessionsWithOrders) })),
      });
    }
    const ordersExtra: IExtraTooltipRow[] = [
      {
        label: 'Cart → Order',
        color: palette.success,
        values: days.map((d) => {
          const ss = d.sessionStats[0];
          return fmtPct(pct(Number(ss.sessionsWithOrders), Number(ss.sessionsWithAddToCart)), 1);
        }),
      },
      {
        label: 'Session → Order',
        color: palette.primary,
        values: days.map((d) => {
          const ss = d.sessionStats[0];
          return fmtPct(pct(Number(ss.sessionsWithOrders), Number(ss.totalSessions)), 1);
        }),
      },
    ];
    if (prevAligned) {
      ordersExtra.push(
        {
          label: 'Cart → Order (prev)',
          color: palette.neutral,
          values: prevAligned.map((d) => {
            const ss = d.sessionStats[0];
            return fmtPct(pct(Number(ss.sessionsWithOrders), Number(ss.sessionsWithAddToCart)), 1);
          }),
        },
        {
          label: 'Session → Order (prev)',
          color: palette.prev,
          values: prevAligned.map((d) => {
            const ss = d.sessionStats[0];
            return fmtPct(pct(Number(ss.sessionsWithOrders), Number(ss.totalSessions)), 1);
          }),
        }
      );
    }
    if (refOrders.current) drawLine(refOrders.current, oSeries, { extraRows: ordersExtra });

    const rSeries: ILineSeries[] = [
      {
        name: 'Engaged → Cart',
        color: palette.warning,
        data: days.map((d) => {
          const ss = d.sessionStats[0];
          return { x: shortDate(d.statDate), y: pct(Number(ss.sessionsWithAddToCart), Number(ss.sessionsWithEvents)) };
        }),
      },
      {
        name: 'Cart → Order',
        color: palette.success,
        data: days.map((d) => {
          const ss = d.sessionStats[0];
          return { x: shortDate(d.statDate), y: pct(Number(ss.sessionsWithOrders), Number(ss.sessionsWithAddToCart)) };
        }),
      },
    ];
    if (prevAligned) {
      rSeries.push({
        name: 'Engaged → Cart (prev)',
        color: palette.warning,
        dashed: true,
        data: prevAligned.map((d, i) => {
          const ss = d.sessionStats[0];
          return { x: shortDate(days[i].statDate), y: pct(Number(ss.sessionsWithAddToCart), Number(ss.sessionsWithEvents)) };
        }),
      });
      rSeries.push({
        name: 'Cart → Order (prev)',
        color: palette.success,
        dashed: true,
        data: prevAligned.map((d, i) => {
          const ss = d.sessionStats[0];
          return { x: shortDate(days[i].statDate), y: pct(Number(ss.sessionsWithOrders), Number(ss.sessionsWithAddToCart)) };
        }),
      });
    }
    if (refRates.current) drawLine(refRates.current, rSeries, { yFmt: (v) => v.toFixed(0) + '%' });

    const et = curr.eventStats.eventTypes;
    const etCur: ISlice[] = [
      { label: 'full', value: et.full, color: palette.primary, labelColor: palette.label.primary },
      { label: 'color', value: et.color, color: palette.warning, labelColor: palette.label.warning },
      { label: 'history', value: et.history, color: palette.info, labelColor: palette.label.info },
      { label: 'unknown', value: et.unknown, color: palette.neutral, labelColor: palette.label.neutral },
    ];
    if (prev && refEvents.current) {
      const pt = prev.eventStats.eventTypes;
      drawDonutCompare(refEvents.current, etCur, [{ value: pt.full }, { value: pt.color }, { value: pt.history }, { value: pt.unknown }], {
        centerLabel: fmt(curr.eventStats.eventCount),
        centerSub: 'events',
      });
    } else if (refEvents.current) {
      drawDonut(refEvents.current, etCur, { donut: true, centerLabel: fmt(curr.eventStats.eventCount), centerSub: 'events' });
    }
  }, [curr, prev, days, prevAligned, resizeTick, themeMode, palette]);

  const sparkSess = useMemo(() => days.map((d) => Number(d.sessionStats[0].totalSessions)), [days]);
  const sparkEng = useMemo(() => days.map((d) => Number(d.sessionStats[0].sessionsWithEvents)), [days]);
  const sparkCart = useMemo(() => days.map((d) => Number(d.sessionStats[0].sessionsWithAddToCart)), [days]);
  const sparkOrd = useMemo(() => days.map((d) => Number(d.sessionStats[0].sessionsWithOrders)), [days]);

  useLayoutEffect(() => {
    const cards = [
      { ref: sparkRefs[0], values: sparkSess, color: palette.primary },
      { ref: sparkRefs[1], values: sparkEng, color: palette.info },
      { ref: sparkRefs[2], values: sparkCart, color: palette.warning },
      { ref: sparkRefs[3], values: sparkOrd, color: palette.success },
    ];
    cards.forEach((c) => {
      if (c.ref.current) drawSpark(c.ref.current, c.values, c.color);
    });
  }, [sparkSess, sparkEng, sparkCart, sparkOrd, resizeTick, themeMode, palette]); // eslint-disable-line react-hooks/exhaustive-deps

  const stages = [
    { label: 'Total sessions', value: s.totalSessions, color: palette.primary },
    { label: 'Engaged', value: s.sessionsWithEvents, color: palette.secondary },
    { label: 'Add to cart', value: s.sessionsWithAddToCart, color: palette.warning },
    { label: 'Orders', value: s.sessionsWithOrders, color: palette.success },
  ];
  const max = stages[0].value || 1;
  const prevStages = p ? [p.totalSessions, p.sessionsWithEvents, p.sessionsWithAddToCart, p.sessionsWithOrders] : null;
  const stepRates = [
    pct(s.sessionsWithEvents, s.totalSessions),
    pct(s.sessionsWithAddToCart, s.sessionsWithEvents),
    pct(s.sessionsWithOrders, s.sessionsWithAddToCart),
  ];
  const prevStepRates = p
    ? [
        pct(p.sessionsWithEvents, p.totalSessions),
        pct(p.sessionsWithAddToCart, p.sessionsWithEvents),
        pct(p.sessionsWithOrders, p.sessionsWithAddToCart),
      ]
    : null;

  return (
    <>
      <div className="section-title">Headline metrics</div>
      <div className="grid grid-4" id="kpi-row">
        <div className="card kpi">
          <div className="kpi-label">Total sessions</div>
          <div className="kpi-value">
            {fmt(s.totalSessions)} {p ? <DeltaBadge curr={s.totalSessions} prev={p.totalSessions} /> : null}
          </div>
          <div className="kpi-sub">
            {fmt(s.uniqueCustomers)} unique · {fmtPct(pct(s.sessionsWithEvents, s.totalSessions), 1)} engaged
          </div>
          <canvas ref={sparkRefs[0]} className="spark" aria-hidden="true" />
        </div>
        <div className="card kpi tinted-info">
          <div className="kpi-label">Engaged sessions</div>
          <div className="kpi-value">
            {fmt(s.sessionsWithEvents)} {p ? <DeltaBadge curr={s.sessionsWithEvents} prev={p.sessionsWithEvents} /> : null}
          </div>
          <div className="kpi-sub">{fmtPct(pct(s.sessionsWithEvents, s.totalSessions), 2)} of total</div>
          <canvas ref={sparkRefs[1]} className="spark" aria-hidden="true" />
        </div>
        <div className="card kpi tinted-warn">
          <div className="kpi-label">Add to cart</div>
          <div className="kpi-value">
            {fmt(s.sessionsWithAddToCart)} {p ? <DeltaBadge curr={s.sessionsWithAddToCart} prev={p.sessionsWithAddToCart} /> : null}
          </div>
          <div className="kpi-sub">{fmtPct(pct(s.sessionsWithAddToCart, s.totalSessions), 2)} of total</div>
          <canvas ref={sparkRefs[2]} className="spark" aria-hidden="true" />
        </div>
        <div className={`card kpi ${orderRate >= 1.8 ? 'tinted-success' : 'tinted-warn'}`}>
          <div className="kpi-label">Orders</div>
          <div className="kpi-value">
            {fmt(s.sessionsWithOrders)} {p ? <DeltaBadge curr={s.sessionsWithOrders} prev={p.sessionsWithOrders} /> : null}
          </div>
          <div className="kpi-sub">
            {fmtPct(orderRate, 2)} overall conversion
            {orderRatePrev != null ? ` · prev ${orderRatePrev.toFixed(2)}%` : ''}
          </div>
          <canvas ref={sparkRefs[3]} className="spark" aria-hidden="true" />
        </div>
      </div>

      <div className="section-title">Conversion funnel</div>
      <div className="grid grid-2">
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Funnel</h3>
                <InfoTip text={TIPS.funnel} />
              </div>
              <div className="sub">Sessions → Engaged → Add to cart → Order</div>
            </div>
          </div>
          <div style={{ padding: '0 8px' }}>
            {stages.map((st, i) => {
              const w = (st.value / max) * 100;
              const prevW = prevStages ? (prevStages[i] / (prevStages[0] || 1)) * 100 : null;
              const pctOf = pct(st.value, max);
              const stepRate = i > 0 ? stepRates[i - 1] : null;
              const prevStepRate = prevStepRates && i > 0 ? prevStepRates[i - 1] : null;
              const stepDelta = stepRate != null && prevStepRate != null ? stepRate - prevStepRate : null;
              return (
                <React.Fragment key={st.label}>
                  {i > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        padding: '3px 0',
                      }}
                    >
                      <div style={{ width: 170, paddingLeft: 10, color: 'var(--text-muted)', fontSize: 11 }}>
                        ↓ conversion
                      </div>
                      <div style={{ flex: 1 }} />
                      <div style={{ width: 160, textAlign: 'right', fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                        {fmtPct(stepRate ?? 0, 1)}
                        {stepDelta != null && (
                          <span
                            className={`delta-inline ${stepDelta > 0.05 ? 'up' : stepDelta < -0.05 ? 'down' : 'flat'}`}
                            style={{ fontSize: 11, marginLeft: 4 }}
                          >
                            {stepDelta > 0 ? '+' : ''}{stepDelta.toFixed(1)}pp
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '10px 0',
                      borderBottom: i < stages.length - 1 ? '1px solid var(--border)' : undefined,
                    }}
                  >
                    <div style={{ width: 170 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{st.label}</div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {fmtPct(pctOf, 2)} of sessions
                      </div>
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: 38 }}>
                      <div style={{ position: 'absolute', left: 0, top: 7, height: 24, width: `${w}%`, background: st.color, borderRadius: 4 }} />
                      {prevW != null && (
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 7,
                            height: 24,
                            width: `${prevW}%`,
                            border: `1.5px dashed ${palette.prev}`,
                            borderRadius: 4,
                            background: 'transparent',
                          }}
                        />
                      )}
                    </div>
                    <div style={{ width: 160, textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(st.value)} {prevStages ? <DeltaBadge curr={st.value} prev={prevStages[i]} /> : null}
                      </div>
                      {prevStages && <div className="muted" style={{ fontSize: 11 }}>prev {fmt(prevStages[i])}</div>}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Stage conversion</h3>
                <InfoTip text={TIPS.rates} />
              </div>
              <div className="sub">Step‑to‑step rates · Δ vs previous</div>
            </div>
          </div>
          <RatesGrid s={s} p={p} />
        </div>
      </div>

      <div className="section-title">Daily trends</div>
      <div className="grid grid-3">
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Sessions</h3>
                <InfoTip text={TIPS.sessions} />
              </div>
              <div className="sub">Total vs. engaged (with events)</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 220 }}>
            <canvas ref={refSessions} role="img" aria-label="Sessions per day" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            <span>
              <span className="swatch" style={{ background: palette.primary }} />
              Total sessions
            </span>
            <span>
              <span className="swatch" style={{ background: palette.success }} />
              Engaged
            </span>
            {prevAligned && (
              <span style={{ opacity: 0.55 }}>
                <span className="swatch" style={{ background: palette.prev, borderRadius: 2 }} />
                Prev period (dashed)
              </span>
            )}
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Engaged rate %</h3>
                <InfoTip text={TIPS.engagedRate} />
              </div>
              <div className="sub">Share of sessions that produced an event</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 220 }}>
            <canvas ref={refEngaged} role="img" aria-label="Engaged rate per day" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            <span>
              <span className="swatch" style={{ background: palette.success }} />
              Engaged rate %
            </span>
            {prevAligned && (
              <span>
                <span className="swatch" style={{ background: palette.success, opacity: 0.45 }} />
                Prev period
              </span>
            )}
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Cart &amp; orders</h3>
                <InfoTip text={TIPS.orders} />
              </div>
              <div className="sub">Add-to-cart and orders per day</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 220 }}>
            <canvas ref={refOrders} role="img" aria-label="Add to cart and orders per day" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            <span>
              <span className="swatch" style={{ background: palette.warning }} />
              Add to cart
            </span>
            <span>
              <span className="swatch" style={{ background: palette.success }} />
              Orders
            </span>
            {prevAligned && (
              <span style={{ opacity: 0.55 }}>
                <span className="swatch" style={{ background: palette.prev, borderRadius: 2 }} />
                Prev period (dashed)
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-2" style={{ marginTop: 14 }}>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Conversion rates over time</h3>
                <InfoTip text={TIPS.rateTrend} />
              </div>
              <div className="sub">Engaged → Cart and Cart → Order, %</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 240 }}>
            <canvas ref={refRates} role="img" aria-label="Conversion rates over time" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            <span>
              <span className="swatch" style={{ background: palette.warning }} />
              Engaged → Cart
            </span>
            <span>
              <span className="swatch" style={{ background: palette.success }} />
              Cart → Order
            </span>
            {prevAligned && (
              <span style={{ opacity: 0.55 }}>
                <span className="swatch" style={{ background: palette.prev, borderRadius: 2 }} />
                Prev period (dashed)
              </span>
            )}
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-head">
            <div>
              <div className="title-row">
                <h3>Event mix</h3>
                <InfoTip text={TIPS.events} />
              </div>
              <div className="sub">Outer ring = current, inner = previous</div>
            </div>
          </div>
          <div className="chart-body" style={{ height: 240 }}>
            <canvas ref={refEvents} role="img" aria-label="Event mix" style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="legend">
            <span>
              <span className="swatch" style={{ background: palette.primary }} />
              full
            </span>
            <span>
              <span className="swatch" style={{ background: palette.warning }} />
              color
            </span>
            <span>
              <span className="swatch" style={{ background: palette.info }} />
              history
            </span>
            <span>
              <span className="swatch" style={{ background: palette.neutral }} />
              unknown
            </span>
          </div>
        </div>
      </div>

      <div className="section-title">
        Daily breakdown <InfoTip text={TIPS.tblDaily} />
      </div>
      <div className="card table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Date</th>
              <th className="num">Total sessions</th>
              <th className="num">Engaged</th>
              <th className="num">Engaged %</th>
              <th className="num">Add to cart</th>
              <th className="num">Engaged → Cart</th>
              <th className="num">Orders</th>
              <th className="num">Cart → Order</th>
              <th className="num">Session → Order</th>
              <th className="num">Customers</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d, i) => {
              const ss = d.sessionStats[0];
              const engagedPct = pct(Number(ss.sessionsWithEvents), Number(ss.totalSessions));
              const cartPct = pct(Number(ss.sessionsWithAddToCart), Number(ss.sessionsWithEvents));
              const orderPct = pct(Number(ss.sessionsWithOrders), Number(ss.sessionsWithAddToCart));
              const sessToOrderPct = pct(Number(ss.sessionsWithOrders), Number(ss.totalSessions));
              const cartCls = cartPct >= 45 ? 'fast' : 'medium';
              const orderCls = orderPct >= 35 ? 'fast' : orderPct >= 30 ? 'medium' : 'slow';
              const sessToOrderCls = sessToOrderPct >= 2 ? 'fast' : sessToOrderPct >= 1 ? 'medium' : 'slow';
              return (
                <React.Fragment key={d.statDate}>
                  <tr>
                    <td>
                      <strong>{d.statDate}</strong>
                    </td>
                    <td className="num">{fmt(ss.totalSessions)}</td>
                    <td className="num">{fmt(ss.sessionsWithEvents)}</td>
                    <td className="num">{engagedPct.toFixed(2)}%</td>
                    <td className="num">{fmt(ss.sessionsWithAddToCart)}</td>
                    <td className="num">
                      <span className={`tag ${cartCls}`}>{cartPct.toFixed(2)}%</span>
                    </td>
                    <td className="num">{fmt(ss.sessionsWithOrders)}</td>
                    <td className="num">
                      <span className={`tag ${orderCls}`}>{orderPct.toFixed(2)}%</span>
                    </td>
                    <td className="num">
                      <span className={`tag ${sessToOrderCls}`}>{sessToOrderPct.toFixed(2)}%</span>
                    </td>
                    <td className="num">{fmt(ss.uniqueCustomers)}</td>
                  </tr>
                  {prevAligned && prevAligned[i] && (
                    <tr className="prev">
                      <td>{prevAligned[i].statDate}</td>
                      <td className="num">
                        {fmt(prevAligned[i].sessionStats[0].totalSessions)}
                        <DeltaInlineNum curr={Number(ss.totalSessions)} prev={Number(prevAligned[i].sessionStats[0].totalSessions)} />
                      </td>
                      <td className="num">
                        {fmt(prevAligned[i].sessionStats[0].sessionsWithEvents)}
                        <DeltaInlineNum curr={Number(ss.sessionsWithEvents)} prev={Number(prevAligned[i].sessionStats[0].sessionsWithEvents)} />
                      </td>
                      <td className="num">{pct(Number(prevAligned[i].sessionStats[0].sessionsWithEvents), Number(prevAligned[i].sessionStats[0].totalSessions)).toFixed(2)}%</td>
                      <td className="num">
                        {fmt(prevAligned[i].sessionStats[0].sessionsWithAddToCart)}
                        <DeltaInlineNum curr={Number(ss.sessionsWithAddToCart)} prev={Number(prevAligned[i].sessionStats[0].sessionsWithAddToCart)} />
                      </td>
                      <td className="num">
                        {pct(Number(prevAligned[i].sessionStats[0].sessionsWithAddToCart), Number(prevAligned[i].sessionStats[0].sessionsWithEvents)).toFixed(2)}%
                      </td>
                      <td className="num">
                        {fmt(prevAligned[i].sessionStats[0].sessionsWithOrders)}
                        <DeltaInlineNum curr={Number(ss.sessionsWithOrders)} prev={Number(prevAligned[i].sessionStats[0].sessionsWithOrders)} />
                      </td>
                      <td className="num">
                        {pct(Number(prevAligned[i].sessionStats[0].sessionsWithOrders), Number(prevAligned[i].sessionStats[0].sessionsWithAddToCart)).toFixed(2)}%
                      </td>
                      <td className="num">
                        {pct(Number(prevAligned[i].sessionStats[0].sessionsWithOrders), Number(prevAligned[i].sessionStats[0].totalSessions)).toFixed(2)}%
                      </td>
                      <td className="num">{fmt(prevAligned[i].sessionStats[0].uniqueCustomers)}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

function DeltaInlineNum({ curr, prev }: { curr: number; prev: number }) {
  const d = prev ? ((curr - prev) / prev) * 100 : null;
  if (d === null || Number.isNaN(d)) return null;
  const dir = d > 0.05 ? 'up' : d < -0.05 ? 'down' : 'flat';
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '–';
  let cls = 'flat';
  if (dir === 'up') cls = 'up';
  else if (dir === 'down') cls = 'down';
  return (
    <span className={`delta-inline ${cls}`}>
      {arrow}
      {Math.abs(d).toFixed(1)}%
    </span>
  );
}

function RatesGrid({ s, p }: { s: IAggregateResult['sessionStats']; p: IAggregateResult['sessionStats'] | null }) {
  const rates = [
    { label: 'Sessions → Engaged', curr: pct(s.sessionsWithEvents, s.totalSessions), prev: p ? pct(p.sessionsWithEvents, p.totalSessions) : null },
    { label: 'Engaged → Cart', curr: pct(s.sessionsWithAddToCart, s.sessionsWithEvents), prev: p ? pct(p.sessionsWithAddToCart, p.sessionsWithEvents) : null },
    { label: 'Cart → Order', curr: pct(s.sessionsWithOrders, s.sessionsWithAddToCart), prev: p ? pct(p.sessionsWithOrders, p.sessionsWithAddToCart) : null },
  ];
  return (
    <div className="grid grid-3" style={{ marginTop: 6 }}>
      {rates.map((r) => {
        const ppDelta = r.prev != null ? r.curr - r.prev : null;
        const ppCls = ppDelta == null ? 'flat' : ppDelta > 0.05 ? 'up' : ppDelta < -0.05 ? 'down' : 'flat';
        const ppTxt =
          ppDelta == null ? null : (
            <span className={`delta-inline ${ppCls}`}>
              {ppDelta > 0 ? '+' : ''}
              {ppDelta.toFixed(2)}pp
            </span>
          );
        return (
          <div key={r.label} className="rate-box">
            <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
              {r.label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
              {r.curr.toFixed(2)}% {ppTxt}
            </div>
            {r.prev != null && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>prev {r.prev.toFixed(2)}%</div>}
          </div>
        );
      })}
    </div>
  );
}

export default MockupLogsConversion;
